import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Loader2, Package, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { CurrencyField } from "@/components/ui/forms";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const emptyForm = { sku_interno: "", nombre: "", codigo_barras: "", nombre_categoria: "", precio_base: "", costo: "", control_inventario: false, estatus: true };

export default function Productos() {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [matriz, setMatriz] = useState({}); // { id_sucursal: {activo, precio_sucursal, stock} }
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, su] = await Promise.all([
        api.get("/productos?incluir_inactivos=true"),
        listSucursales(),
      ]);
      setItems(p.data);
      setSucursales(su.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm);
    setMatriz(Object.fromEntries(sucursales.map((s) => [s.id_sucursal, { activo: false, precio_sucursal: "", stock: "" }])));
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ sku_interno: p.sku_interno, nombre: p.nombre || "", codigo_barras: p.codigo_barras || "", nombre_categoria: p.nombre_categoria || "", precio_base: String(p.precio_base), costo: p.costo != null ? String(p.costo) : "", control_inventario: p.control_inventario, estatus: p.estatus });
    const m = Object.fromEntries(sucursales.map((s) => [s.id_sucursal, { activo: false, precio_sucursal: "", stock: "" }]));
    (p.sucursales || []).forEach((c) => { m[c.id_sucursal] = { activo: c.activo, precio_sucursal: c.precio_sucursal != null ? String(c.precio_sucursal) : "", stock: c.stock != null ? String(c.stock) : "" }; });
    setMatriz(m); setOpen(true);
  };

  const setRow = (id, patch) => setMatriz((m) => ({ ...m, [id]: { ...m[id], ...patch } }));

  const save = async () => {
    if (!form.sku_interno.trim()) return toast.error("El SKU interno es obligatorio");
    setSaving(true);
    try {
      const sucs = Object.entries(matriz)
        .filter(([, v]) => v.activo || (v.precio_sucursal !== "" && v.precio_sucursal != null) || (v.stock !== "" && v.stock != null))
        .map(([id, v]) => ({
          id_sucursal: id, activo: !!v.activo,
          precio_sucursal: v.precio_sucursal === "" || v.precio_sucursal == null ? null : Number(Number(v.precio_sucursal).toFixed(2)),
          stock: v.stock === "" || v.stock == null ? 0 : parseInt(v.stock, 10),
        }));
      const payload = {
        sku_interno: form.sku_interno.trim(), nombre: form.nombre || null, codigo_barras: form.codigo_barras || null,
        nombre_categoria: form.nombre_categoria || null,
        precio_base: Number(Number(form.precio_base || 0).toFixed(2)),
        costo: form.costo === "" || form.costo == null ? null : Number(Number(form.costo).toFixed(2)),
        control_inventario: form.control_inventario,
        sucursales: sucs,
      };
      if (editing) {
        await api.put(`/productos/${editing.id_producto}`, { ...payload, estatus: form.estatus });
        toast.success("Producto actualizado");
      } else {
        await api.post("/productos", payload);
        toast.success("Producto creado");
      }
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doToggle = async (p) => {
    try {
      if (p.estatus) await api.delete(`/productos/${p.id_producto}`);
      else await api.put(`/productos/${p.id_producto}`, { estatus: true });
      toast.success(p.estatus ? "Producto desactivado" : "Producto reactivado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Catálogo del inquilino</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Productos</h1>
          </div>
          <button onClick={openCreate} data-testid="create-producto-btn"
            className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nuevo producto
          </button>
        </div>

        <div className="bg-white border border-[#e4e4e7]">
          {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          : items.length === 0 ? <div className="p-16 text-center text-[#52525B]"><Package className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin productos registrados.</p></div>
          : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="productos-table">
              <thead><tr className="border-b border-[#e4e4e7] text-left">
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">SKU</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Producto</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Cód. barras</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Precio</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Inventario</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Estatus</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase text-right">Acciones</th>
              </tr></thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id_producto} className="border-b border-[#f0f0f1] hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-5 py-4 font-mono font-semibold">{p.sku_interno}</td>
                    <td className="px-5 py-4">{p.nombre || "—"}</td>
                    <td className="px-5 py-4 font-mono text-[#52525B]">{p.codigo_barras || "—"}</td>
                    <td className="px-5 py-4 font-mono">${Number(p.precio_base).toFixed(2)}</td>
                    <td className="px-5 py-4">{p.control_inventario ? <span className="text-[10px] font-bold uppercase tracking-wider text-[#047857]">Sí</span> : <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">No</span>}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${p.estatus ? "text-[#047857]" : "text-[#B45309]"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${p.estatus ? "bg-[#10B981]" : "bg-[#F59E0B]"}`} />{p.estatus ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right"><div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(p)} data-testid={`edit-producto-${p.id_producto}`} className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setConfirm(p)} data-testid={`toggle-producto-${p.id_producto}`}
                        className={`h-8 px-3 inline-flex items-center gap-1.5 border border-[#e4e4e7] text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${p.estatus ? "text-[#B45309] hover:border-[#F59E0B]" : "text-[#047857] hover:border-[#10B981]"}`}>
                        {p.estatus ? <><PowerOff className="h-3.5 w-3.5" /> Desactivar</> : <><Power className="h-3.5 w-3.5" /> Reactivar</>}
                      </button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </motion.div>

      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto border-l-[#0A0A0A]">
          <SheetHeader><SheetTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Editar producto" : "Nuevo producto"}</SheetTitle><SheetDescription>Configura el producto y su disponibilidad/stock por sucursal.</SheetDescription></SheetHeader>
          <div className="space-y-5 mt-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">SKU interno</Label>
                <Input value={form.sku_interno} onChange={(e) => setForm({ ...form, sku_interno: e.target.value })} data-testid="producto-sku" className="h-11 rounded-none border-[#0A0A0A]" /></div>
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Código de barras</Label>
                <Input value={form.codigo_barras} onChange={(e) => setForm({ ...form, codigo_barras: e.target.value })} data-testid="producto-codigo" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            </div>
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="producto-nombre" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Categoría</Label>
                <Input value={form.nombre_categoria} onChange={(e) => setForm({ ...form, nombre_categoria: e.target.value })} data-testid="producto-categoria" className="h-11 rounded-none border-[#0A0A0A]" /></div>
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Precio base</Label>
                <CurrencyField value={form.precio_base} onChange={(v) => setForm({ ...form, precio_base: v })} data-testid="producto-precio" className="h-11 rounded-none border-[#0A0A0A]" /></div>
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Costo</Label>
                <CurrencyField value={form.costo} onChange={(v) => setForm({ ...form, costo: v })} data-testid="producto-costo" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            </div>
            <div className="flex items-center justify-between border border-[#e4e4e7] p-3">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Control de inventario</Label>
              <Switch checked={form.control_inventario} onCheckedChange={(v) => setForm({ ...form, control_inventario: v })} data-testid="producto-control-inv" className="data-[state=checked]:bg-[#10B981]" />
            </div>
            {editing && (
              <div className="flex items-center justify-between border border-[#e4e4e7] p-3">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Activo (estatus)</Label>
                <Switch checked={form.estatus} onCheckedChange={(v) => setForm({ ...form, estatus: v })} data-testid="producto-estatus" className="data-[state=checked]:bg-[#10B981]" />
              </div>
            )}

            <div className="border border-[#e4e4e7] p-4 space-y-3">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] block">Disponibilidad por sucursal</Label>
              {sucursales.length === 0 ? <p className="text-xs text-[#52525B]">No hay sucursales.</p> : (
                <div className="space-y-2" data-testid="producto-matriz">
                  {sucursales.map((s) => {
                    const row = matriz[s.id_sucursal] || { activo: false, precio_sucursal: "", stock: "" };
                    return (
                      <div key={s.id_sucursal} className="flex items-center gap-2 bg-[#FAFAFA] border border-[#e4e4e7] px-3 py-2">
                        <Switch checked={row.activo} onCheckedChange={(v) => setRow(s.id_sucursal, { activo: v })} data-testid={`pr-activo-${s.id_sucursal}`} className="data-[state=checked]:bg-[#0A0A0A]" />
                        <span className="text-sm font-medium flex-1 truncate">{s.nombre}</span>
                        <CurrencyField placeholder="Precio" value={row.precio_sucursal}
                          onChange={(v) => setRow(s.id_sucursal, { precio_sucursal: v })}
                          data-testid={`pr-precio-${s.id_sucursal}`} className="h-9 w-24 rounded-none border-[#0A0A0A] text-sm" />
                        <Input type="number" placeholder="Stock" value={row.stock} disabled={!form.control_inventario}
                          onChange={(e) => setRow(s.id_sucursal, { stock: e.target.value })}
                          data-testid={`pr-stock-${s.id_sucursal}`} className="h-9 w-20 rounded-none border-[#0A0A0A] text-sm disabled:bg-gray-100 disabled:opacity-60" />
                      </div>
                    );
                  })}
                </div>
              )}
              {!form.control_inventario && <p className="text-[11px] text-[#52525B]">El campo de stock se habilita al activar "Control de inventario".</p>}
            </div>

            <button onClick={save} disabled={saving} data-testid="producto-save-btn"
              className="w-full h-12 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#27272A] transition-colors duration-200 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear producto"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.estatus ? "¿Desactivar producto?" : "¿Reactivar producto?"}
        description={confirm?.estatus ? `Se desactivará "${confirm?.sku_interno}". No se elimina; podrás reactivarlo.` : `Se reactivará "${confirm?.sku_interno}".`}
        confirmLabel={confirm?.estatus ? "Desactivar" : "Reactivar"}
        danger={confirm?.estatus}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) doToggle(c); }}
      />
    </DashboardLayout>
  );
}
